import React, { useState, useEffect } from 'react';
import { AIScenarioService } from '../services/ai-scenario.service';
import { AIScenario } from '../types/ai-scenario';

const TestAIScenario: React.FC = () => {
  const [scenarios, setScenarios] = useState<AIScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<AIScenario | null>(null);
  const [testInput, setTestInput] = useState('{"content": "这是一个测试内容", "max_length": 100}');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const data = await AIScenarioService.getAvailableScenarios();
      setScenarios(data);
      if (data.length > 0) {
        setSelectedScenario(data[0]);
      }
    } catch (error) {
      console.error('加载场景失败:', error);
    }
  };

  const testScenario = () => {
    if (!selectedScenario) return;

    try {
      setLoading(true);
      const inputData = JSON.parse(testInput);
      const processedPrompt = AIScenarioService.processPromptTemplate(
        selectedScenario.prompt_template,
        inputData
      );
      setResult(processedPrompt);
    } catch (error) {
      console.error('测试失败:', error);
      setResult('错误: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">AI场景测试页面</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 场景选择和输入 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">场景配置</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择场景
              </label>
              <select
                value={selectedScenario?.id || ''}
                onChange={(e) => {
                  const scenario = scenarios.find(s => s.id === parseInt(e.target.value));
                  setSelectedScenario(scenario || null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">请选择场景</option>
                {scenarios.map(scenario => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.scenario_name} ({scenario.scenario_code})
                  </option>
                ))}
              </select>
            </div>

            {selectedScenario && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  场景描述
                </label>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {selectedScenario.description || '无描述'}
                </p>
              </div>
            )}

            {selectedScenario && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  提示词模板
                </label>
                <pre className="text-sm text-gray-600 bg-gray-50 p-3 rounded whitespace-pre-wrap border">
                  {selectedScenario.prompt_template}
                </pre>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                测试输入 (JSON格式)
              </label>
              <textarea
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder='{"content": "测试内容", "max_length": 200}'
              />
            </div>

            <button
              onClick={testScenario}
              disabled={!selectedScenario || loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? '处理中...' : '测试场景'}
            </button>
          </div>

          {/* 结果显示 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">处理结果</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                处理后的提示词
              </label>
              <div className="border border-gray-300 rounded-md p-3 bg-gray-50 min-h-[200px]">
                <pre className="text-sm whitespace-pre-wrap">
                  {result || '点击"测试场景"查看结果'}
                </pre>
              </div>
            </div>

            {result && (
              <button
                onClick={() => navigator.clipboard.writeText(result)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                复制结果
              </button>
            )}
          </div>
        </div>

        {/* 场景列表 */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">可用场景列表</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.map(scenario => (
              <div key={scenario.id} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">
                  {scenario.scenario_name}
                </h3>
                <p className="text-sm text-gray-500 mb-2">
                  编码: <code className="bg-gray-100 px-1 rounded">{scenario.scenario_code}</code>
                </p>
                <p className="text-sm text-gray-600">
                  {scenario.description || '无描述'}
                </p>
                <div className="mt-2 text-xs text-gray-400">
                  温度: {scenario.temperature} | Token: {scenario.max_tokens}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestAIScenario;